import { BoloQueuesBundle, BoloQueueView } from '@mark43/cad-api';
import { createContext, ReactNode, useContext, useMemo, useRef } from 'react';

type ContextType = {
    boloQueueViewsRef: React.MutableRefObject<Record<number, BoloQueueView>>;
    getListeners: () => BoloQueueViewsListener[];
    addListener: (listener: BoloQueueViewsListener) => void;
    removeListener: (listener: BoloQueueViewsListener) => void;
};

const BoloQueueViewsContext = createContext<ContextType | undefined>(undefined);

export type BoloQueueViewsListener = {
    onBootstrap: () => void;
    onUpdate: (bundle: BoloQueuesBundle) => void;
};

export function BoloQueueViewsProvider({ children }: { children: ReactNode }) {
    const boloQueueViewsRef = useRef<ContextType['boloQueueViewsRef']['current']>({});
    const listenersRef = useRef<BoloQueueViewsListener[]>([]);
    const contextValue = useMemo(
        () => ({
            boloQueueViewsRef,
            getListeners() {
                return listenersRef.current;
            },
            addListener: (listener: BoloQueueViewsListener) => {
                if (!listenersRef.current.includes(listener)) listenersRef.current.push(listener);
            },
            removeListener: (listener: BoloQueueViewsListener) => {
                const index = listenersRef.current.indexOf(listener);
                if (index !== -1) {
                    listenersRef.current.splice(index, 1);
                }
            },
        }),
        []
    );
    return (
        <BoloQueueViewsContext.Provider value={contextValue}>
            {children}
        </BoloQueueViewsContext.Provider>
    );
}

export function useBoloQueueViews(): ContextType {
    const context = useContext(BoloQueueViewsContext);
    if (!context) {
        throw new Error('Unexpectedly could not find bolo queue views context value');
    }
    return context;
}
